import {
  CustomMappingType,
  ICustomMapping,
  IPracticeMigration,
} from '@principle-theorem/practice-migrations/interfaces';
import {
  ISO_DATE_TIME_FORMAT,
  Timestamp,
  Timezone,
  WithRef,
  toInt,
  toTimestamp,
} from '@principle-theorem/shared';
import { isString } from 'lodash';
import * as moment from 'moment-timezone';
import { CustomMapping } from '../../../custom-mapping';
import { SingleValueMappingHandler } from '../../../mappings/single-value';
import { TranslationMapHandler } from '../../../translation-map';
import { IPraktikaAppointment } from '../../source/entities/patient-appointment';

export const APPOINTMENT_STEP_SIZE_CUSTOM_MAPPING_TYPE =
  'appointmentStepSizeCustomMapping';

const defaultDuration = '15';

const PRAKTIKA_STEP_SIZE_OPTIONS = ['5', '10', '15'];

export const APPOINTMENT_STEP_SIZE_MAPPING: ICustomMapping = CustomMapping.init(
  {
    metadata: {
      label: 'Appointment Step Size',
      description: 'Minutes per Praktika appointment slot.',
      type: APPOINTMENT_STEP_SIZE_CUSTOM_MAPPING_TYPE,
    },
    type: CustomMappingType.SingleValue,
    inputType: 'select',
    placeholder: 'Select Appointment Step Size',
    hint: 'Default step size is 15 minutes.',
    options: PRAKTIKA_STEP_SIZE_OPTIONS.map((value) => ({
      label: `${value} Minutes`,
      value,
    })),
  }
);

export class PraktikaAppointmentStepSizeMappingHandler extends SingleValueMappingHandler {
  customMapping = APPOINTMENT_STEP_SIZE_MAPPING;

  async getAppointmentDuration(
    migration: WithRef<IPracticeMigration>,
    appointment: IPraktikaAppointment
  ): Promise<number> {
    const translationMap = new TranslationMapHandler(migration);
    const stepSize = await this.getBySource(
      this.customMapping.metadata.type,
      translationMap
    );

    const value = stepSize?.destinationValue?.value;
    const minutes =
      isString(value) &&
      PRAKTIKA_STEP_SIZE_OPTIONS.includes(value)
        ? toInt(value)
        : toInt(defaultDuration);
    return appointment.appointment_duration * minutes;
  }

  async getAppointmentEndTime(
    migration: WithRef<IPracticeMigration>,
    appointment: IPraktikaAppointment,
    timezone: Timezone
  ): Promise<Timestamp | undefined> {
    const duration = await this.getAppointmentDuration(migration, appointment);
    return appointment.appointment_starttime
      ? toTimestamp(
          moment
            .tz(
              appointment.appointment_starttime,
              ISO_DATE_TIME_FORMAT,
              timezone
            )
            .add({ minutes: duration })
        )
      : undefined;
  }
}
